import { Button } from '@/components/ui/button'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { formatCustomerAddressLine } from '@/lib/services/customers/customers.address'
import { customerQueries } from '@/lib/services/customers/customers.query'
import { useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { Mail, MapPin, Phone, X } from 'lucide-react'
import { useState } from 'react'
import { CustomerSearchInput } from './CustomerSearchInput'

function getCartCustomerId(cart) {
  return (
    Number(cart?.purchaser_id ?? cart?.purchaser?.id ?? cart?.purchaser_customer_id ?? 0) || null
  )
}

function RecipientOption({ recipient }) {
  const addressLine = formatCustomerAddressLine(recipient.delivery_address)

  return (
    <div className="flex w-full min-w-0 flex-col gap-1 py-1">
      <span className="truncate text-sm font-semibold">
        {recipient.name || recipient.email || recipient.phone || 'Previous Recipient'}
      </span>
      <div className="space-y-1 text-xs text-muted-foreground">
        {recipient.phone ? (
          <div className="flex items-center gap-2">
            <Phone className="size-3.5" />
            <span className="truncate">{formatPhone(recipient.phone)}</span>
          </div>
        ) : null}
        {recipient.email ? (
          <div className="flex items-center gap-2">
            <Mail className="size-3.5" />
            <span className="truncate">{recipient.email}</span>
          </div>
        ) : null}
        {addressLine ? (
          <div className="flex items-center gap-2">
            <MapPin className="size-3.5" />
            <span className="truncate">{addressLine}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function RecipientSearch({
  cart,
  fulfillmentType = null,
  onClearForm,
  onRecipientSelected,
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const queryClient = useQueryClient()
  const { orgId } = useParams({ strict: false })
  const customerId = getCartCustomerId(cart)

  if (!customerId) {
    return null
  }

  const fetchRecipientOptions = (search) => {
    if (!customerId) return []

    return queryClient.fetchQuery(
      customerQueries.recipientSuggestions({
        organizationId: orgId,
        customerId,
        filters: {
          search,
          ...(fulfillmentType ? { fulfillment_type: fulfillmentType } : {}),
        },
      })
    )
  }

  const selectRecipient = (recipient) => {
    setSearchTerm(recipient.name || recipient.email || recipient.phone || '')
    onRecipientSelected?.(recipient)
  }

  const clearForm = () => {
    setSearchTerm('')
    onClearForm?.()
  }

  return (
    <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <CustomerSearchInput
          id={`${fulfillmentType || 'pos'}-recipient-search`}
          label="Previous Recipient"
          inputClassName="h-10"
          placeholder="Search this customer's previous recipients..."
          value={searchTerm}
          onChange={setSearchTerm}
          minChars={0}
          fetchOptions={fetchRecipientOptions}
          onOptionSelected={selectRecipient}
          renderOption={(recipient) => <RecipientOption recipient={recipient} />}
        />
        <Button type="button" variant="outline" className="h-10 sm:mb-0" onClick={clearForm}>
          <X className="size-4" />
          Clear Form
        </Button>
      </div>
    </div>
  )
}
